import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Checkbox } from '../ui/checkbox';
import { AlertCircle, CheckCircle, XCircle, Tag, Settings, Check, ChevronsUpDown, Info, Loader2, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import TopicPickerMulti from '../shared/TopicPickerMulti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Nautical grades - surveyors can come from any MMD
const NAUTICAL_GRADES = ['Master', 'Chief Mate', '2MFG'];
const isNauticalGrade = (grade) => NAUTICAL_GRADES.includes(grade);

// Bulk Surveyor Edit Component
const BulkSurveyorEdit = ({ submission, surveyors, onUpdate }) => {
  const [internalSurveyorId, setInternalSurveyorId] = useState('none');
  const [externalSurveyorId, setExternalSurveyorId] = useState('none');
  const [isApplying, setIsApplying] = useState(false);
  const [internalComboOpen, setInternalComboOpen] = useState(false);
  const [externalComboOpen, setExternalComboOpen] = useState(false);

  const isNautical = isNauticalGrade(submission.grade);

  // Get current surveyor assignments and user-entered names
  const getCurrentAssignments = () => {
    const assignments = {
      internal: { ids: new Set(), others: new Set(), names: new Set() },
      external: { ids: new Set(), others: new Set(), names: new Set() }
    };
    
    submission.questions.forEach(q => {
      // Collect IDs
      if (q.surveyor_internal_id) assignments.internal.ids.add(q.surveyor_internal_id);
      if (q.surveyor_external_id) assignments.external.ids.add(q.surveyor_external_id);
      
      // Collect "Other" names
      if (q.surveyor_internal_other) assignments.internal.others.add(q.surveyor_internal_other);
      if (q.surveyor_external_other) assignments.external.others.add(q.surveyor_external_other);
    });
    
    // Get surveyor names for IDs
    assignments.internal.ids.forEach(id => {
      const surveyor = surveyors.find(s => s.id === id);
      if (surveyor) assignments.internal.names.add(surveyor.name);
    });
    assignments.external.ids.forEach(id => {
      const surveyor = surveyors.find(s => s.id === id);
      if (surveyor) assignments.external.names.add(surveyor.name);
    });
    
    return {
      internal: {
        names: Array.from(assignments.internal.names),
        others: Array.from(assignments.internal.others)
      },
      external: {
        names: Array.from(assignments.external.names),
        others: Array.from(assignments.external.others)
      }
    };
  };

  const currentAssignments = getCurrentAssignments();

  const handleBulkApply = async () => {
    if (internalSurveyorId === 'none' && externalSurveyorId === 'none') {
      toast.error('Please select at least one surveyor (internal or external)');
      return;
    }

    setIsApplying(true);
    try {
      const updatePromises = submission.questions.map(question => {
        // Each question gets the appropriate surveyor based on its asked_by_internal flag
        const updateData = {
          surveyor_internal_id: null,
          surveyor_external_id: null,
          surveyor_internal_other: null,
          surveyor_external_other: null
        };

        if (question.asked_by_internal) {
          // Asked by internal: assign internal surveyor and clear external
          if (internalSurveyorId !== 'none') {
            updateData.surveyor_internal_id = internalSurveyorId;
          }
        } else {
          // Asked by external: assign external surveyor and clear internal
          if (externalSurveyorId !== 'none') {
            updateData.surveyor_external_id = externalSurveyorId;
          }
        }

        return axios.put(`${API}/questions/${question.id}`, updateData);
      });

      const responses = await Promise.all(updatePromises);
      const updatedQuestions = responses.map(r => r.data);
      
      onUpdate(updatedQuestions);
    } catch (error) {
      console.error('Failed to bulk update:', error);
      toast.error('Failed to update questions');
    } finally {
      setIsApplying(false);
    }
  };

  // Get unique functions in this submission for filtering
  const functionIds = [...new Set(submission.questions.map(q => q.function_id))];

  // For Nautical grades: Show all surveyors for the grade (any MMD), grouped by home MMD
  // For Engineering grades: Filter by submitted MMD
  const getFilteredSurveyors = (type) => {
    const gradeSurveyors = surveyors.filter(s => 
      s.type === type && s.grades?.includes(submission.grade)
    );
    
    if (isNautical) {
      // For Nautical: Return all surveyors for the grade, grouped by home MMD
      return gradeSurveyors;
    } else {
      // For Engineering: Filter by MMD
      // Handle both old format ("Kolkata") and new format ("Kolkata MMD")
      const mmd = submission.mmd_location;
      const mmdNormalized = mmd?.endsWith(' MMD') ? mmd : `${mmd} MMD`;
      return gradeSurveyors.filter(s => s.mmd_locations?.includes(mmd) || s.mmd_locations?.includes(mmdNormalized));
    }
  };

  // Group surveyors by MMD for Nautical grades
  const groupSurveyorsByMMD = (surveyorList) => {
    const grouped = {};
    surveyorList.forEach(s => {
      const homeMmd = s.mmd_locations?.[0] || 'Unknown';
      if (!grouped[homeMmd]) grouped[homeMmd] = [];
      grouped[homeMmd].push(s);
    });
    return grouped;
  };

  const internalSurveyors = getFilteredSurveyors('internal');
  const externalSurveyors = getFilteredSurveyors('external');
  
  const internalGrouped = isNautical ? groupSurveyorsByMMD(internalSurveyors) : null;
  const externalGrouped = isNautical ? groupSurveyorsByMMD(externalSurveyors) : null;

  const getSurveyorDisplayName = (surveyorId, surveyorList) => {
    if (surveyorId === 'none') return 'No Change';
    const s = surveyorList.find(sv => sv.id === surveyorId);
    return s ? `${s.name}${s.is_active === false ? ' (Inactive)' : ''}` : 'Select surveyor...';
  };

  return (
    <div className="space-y-3">
      {/* Current Assignments */}
      <div className="p-3 bg-blue-50 border border-blue-300 rounded text-xs">
        <p className="font-semibold text-blue-900 mb-2">📋 Current Assignments:</p>
        
        {/* Internal */}
        <div className="mb-2">
          <p className="font-medium text-blue-800">Internal:</p>
          {currentAssignments.internal.names.length > 0 && (
            <p className="text-blue-700 ml-2">• {currentAssignments.internal.names.join(', ')}</p>
          )}
          {currentAssignments.internal.others.length > 0 && (
            <p className="text-amber-700 ml-2">
              • Other: {currentAssignments.internal.others.join(', ')}
            </p>
          )}
          {currentAssignments.internal.names.length === 0 && currentAssignments.internal.others.length === 0 && (
            <p className="text-gray-500 ml-2">• Not assigned</p>
          )}
        </div>
        
        {/* External */}
        <div>
          <p className="font-medium text-blue-800">External:</p>
          {currentAssignments.external.names.length > 0 && (
            <p className="text-blue-700 ml-2">• {currentAssignments.external.names.join(', ')}</p>
          )}
          {currentAssignments.external.others.length > 0 && (
            <p className="text-amber-700 ml-2">
              • Other: {currentAssignments.external.others.join(', ')}
            </p>
          )}
          {currentAssignments.external.names.length === 0 && currentAssignments.external.others.length === 0 && (
            <p className="text-gray-500 ml-2">• Not assigned</p>
          )}
        </div>
      </div>

      {/* Internal Surveyor - Searchable */}
      <div>
        <Label className="text-sm font-medium mb-1 block">New Internal Surveyor</Label>
        <Popover open={internalComboOpen} onOpenChange={setInternalComboOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={internalComboOpen}
              className="w-full justify-between h-9 text-sm"
            >
              {getSurveyorDisplayName(internalSurveyorId, internalSurveyors)}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search internal surveyors..." />
              <CommandList>
                <CommandEmpty>No surveyors found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="none"
                    onSelect={() => {
                      setInternalSurveyorId('none');
                      setInternalComboOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", internalSurveyorId === 'none' ? "opacity-100" : "opacity-0")} />
                    No Change
                  </CommandItem>
                </CommandGroup>
                {internalSurveyors.length === 0 ? (
                  <div className="p-2 text-xs text-gray-500 text-center">
                    No internal surveyors for {isNautical ? submission.grade : submission.mmd_location}
                  </div>
                ) : isNautical && internalGrouped ? (
                  // Grouped by MMD for Nautical grades
                  Object.entries(internalGrouped).map(([mmd, mmdSurveyors]) => (
                    <CommandGroup key={mmd} heading={mmd}>
                      {mmdSurveyors.map(s => (
                        <CommandItem
                          key={s.id}
                          value={`${s.name} ${mmd}`}
                          onSelect={() => {
                            setInternalSurveyorId(s.id);
                            setInternalComboOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", internalSurveyorId === s.id ? "opacity-100" : "opacity-0")} />
                          {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))
                ) : (
                  // Flat list for Engineering grades
                  <CommandGroup heading="Internal Surveyors">
                    {internalSurveyors.map(s => (
                      <CommandItem
                        key={s.id}
                        value={s.name}
                        onSelect={() => {
                          setInternalSurveyorId(s.id);
                          setInternalComboOpen(false);
                        }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", internalSurveyorId === s.id ? "opacity-100" : "opacity-0")} />
                        {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <p className="text-xs text-gray-500 mt-1">
          {isNautical 
            ? `Showing all ${submission.grade} surveyors (any MMD) • Applied to "asked by internal"`
            : `Filtered by ${submission.mmd_location} • Applied to "asked by internal"`}
        </p>
      </div>

      {/* External Surveyor - Searchable */}
      <div>
        <Label className="text-sm font-medium mb-1 block">New External Surveyor</Label>
        <Popover open={externalComboOpen} onOpenChange={setExternalComboOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={externalComboOpen}
              className="w-full justify-between h-9 text-sm"
            >
              {getSurveyorDisplayName(externalSurveyorId, externalSurveyors)}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search external surveyors..." />
              <CommandList>
                <CommandEmpty>No surveyors found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="none"
                    onSelect={() => {
                      setExternalSurveyorId('none');
                      setExternalComboOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", externalSurveyorId === 'none' ? "opacity-100" : "opacity-0")} />
                    No Change
                  </CommandItem>
                </CommandGroup>
                {externalSurveyors.length === 0 ? (
                  <div className="p-2 text-xs text-gray-500 text-center">
                    No external surveyors for {isNautical ? submission.grade : submission.mmd_location}
                  </div>
                ) : isNautical && externalGrouped ? (
                  // Grouped by MMD for Nautical grades
                  Object.entries(externalGrouped).map(([mmd, mmdSurveyors]) => (
                    <CommandGroup key={mmd} heading={mmd}>
                      {mmdSurveyors.map(s => (
                        <CommandItem
                          key={s.id}
                          value={`${s.name} ${mmd}`}
                          onSelect={() => {
                            setExternalSurveyorId(s.id);
                            setExternalComboOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", externalSurveyorId === s.id ? "opacity-100" : "opacity-0")} />
                          {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))
                ) : (
                  // Flat list for Engineering grades
                  <CommandGroup heading="External Surveyors">
                    {externalSurveyors.map(s => (
                      <CommandItem
                        key={s.id}
                        value={s.name}
                        onSelect={() => {
                          setExternalSurveyorId(s.id);
                          setExternalComboOpen(false);
                        }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", externalSurveyorId === s.id ? "opacity-100" : "opacity-0")} />
                        {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <p className="text-xs text-gray-500 mt-1">
          {isNautical 
            ? `Showing all ${submission.grade} surveyors (any MMD) • Applied to "NOT asked by internal"`
            : `Filtered by ${submission.mmd_location} • Applied to "NOT asked by internal"`}
        </p>
      </div>

      <Button
        size="sm"
        onClick={handleBulkApply}
        disabled={isApplying || (internalSurveyorId === 'none' && externalSurveyorId === 'none')}
        className="w-full bg-amber-600 hover:bg-amber-700"
      >
        {isApplying ? 'Applying...' : `Apply to All ${submission.questions.length} Questions`}
      </Button>
    </div>
  );
};

// Helper to generate a short persistent ID from submission_batch_id or key
const getSubmissionNumber = (submissionId) => {
  if (!submissionId) return '???';
  // Use last 4 characters of the ID for a short, consistent reference
  const shortId = submissionId.slice(-4).toUpperCase();
  return shortId;
};

const QuestionReview = ({ onRefresh }) => {
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [bulkEditSubmission, setBulkEditSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState({});
  const [topics, setTopics] = useState([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [topicSearchQuery, setTopicSearchQuery] = useState('');
  const [functions, setFunctions] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [similarQuestions, setSimilarQuestions] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  
  // Combobox states for searchable surveyor dropdowns
  const [internalSurveyorComboOpen, setInternalSurveyorComboOpen] = useState(false);
  const [externalSurveyorComboOpen, setExternalSurveyorComboOpen] = useState(false);
  
  // Merge dialog state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTargetQuestion, setMergeTargetQuestion] = useState(null);
  const [mergeOptions, setMergeOptions] = useState({
    updateAnswer: false
  });
  const [isMerging, setIsMerging] = useState(false);

  // Group questions into submissions - defined before useEffect to avoid hoisting issues
  const groupQuestionsIntoSubmissions = async (questions, preserveBatchId = null, currentSelectedQuestion = null, currentSelectedSubmission = null) => {
    const submissionMap = {};
    
    questions.forEach((question) => {
      let key;
      if (question.submission_batch_id) {
        key = question.submission_batch_id;
      } else {
        const submissionDate = question.created_at ? question.created_at.split('T')[0] : 'unknown';
        key = `legacy_${question.submitted_by_user_id || 'unknown'}_${submissionDate}_${question.grade}_${question.mmd_location}_${question.exam_month}`;
      }
      
      if (!submissionMap[key]) {
        submissionMap[key] = {
          id: key,
          submitted_by_user_id: question.submitted_by_user_id,
          grade: question.grade,
          mmd_location: question.mmd_location,
          exam_month: question.exam_month,
          created_at: question.created_at,
          submission_batch_id: question.submission_batch_id,
          questions: []
        };
      }
      
      submissionMap[key].questions.push(question);
    });
    
    // Fetch user details for all unique user IDs
    const userIds = [...new Set(Object.values(submissionMap).map(s => s.submitted_by_user_id).filter(Boolean))];
    if (userIds.length > 0) {
      try {
        const userDetailsPromises = userIds.map(userId => 
          axios.get(`${API}/users/${userId}/indos`).catch(() => null)
        );
        const userDetailsResponses = await Promise.all(userDetailsPromises);
        const userDetailsMap = {};
        userDetailsResponses.forEach((response, index) => {
          if (response && response.data) {
            userDetailsMap[userIds[index]] = response.data.indos_number;
          }
        });
        
        Object.values(submissionMap).forEach(submission => {
          if (submission.submitted_by_user_id && userDetailsMap[submission.submitted_by_user_id]) {
            submission.user_indos = userDetailsMap[submission.submitted_by_user_id];
          }
        });
      } catch (error) {
        console.error('Failed to fetch user details:', error);
      }
    }
    
    const submissionsArray = Object.values(submissionMap).sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );
    
    setSubmissions(submissionsArray);
    
    const resetEditDataForQuestion = (question) => {
      if (question) {
        setEditData({
          question_text: question.question_text,
          answer_text: question.answer_text || '',
          topic_id: question.topic_id || '',
          asked_by_internal: question.asked_by_internal || false,
          surveyor_internal_id: question.surveyor_internal_id || null,
          surveyor_external_id: question.surveyor_external_id || null
        });
        // Hydrate multi-select topic state: prefer `topic_ids` array,
        // fall back to legacy single `topic_id` if present.
        const ids = question.topic_ids?.length > 0
          ? question.topic_ids
          : (question.topic_id ? [question.topic_id] : []);
        setSelectedTopicIds(ids);
        setTopicSearchQuery('');
      } else {
        setEditData({});
        setSelectedTopicIds([]);
        setTopicSearchQuery('');
      }
    };
    
    if (preserveBatchId) {
      const restoredSubmission = submissionsArray.find(s => s.id === preserveBatchId);
      if (restoredSubmission && restoredSubmission.questions.length > 0) {
        setSelectedSubmission(restoredSubmission);
        if (currentSelectedQuestion) {
          const restoredQuestion = restoredSubmission.questions.find(q => q.id === currentSelectedQuestion.id);
          if (restoredQuestion) {
            setSelectedQuestion(restoredQuestion);
            resetEditDataForQuestion(restoredQuestion);
          } else {
            const nextQuestion = restoredSubmission.questions[0];
            setSelectedQuestion(nextQuestion);
            resetEditDataForQuestion(nextQuestion);
          }
        } else {
          const nextQuestion = restoredSubmission.questions[0];
          setSelectedQuestion(nextQuestion);
          resetEditDataForQuestion(nextQuestion);
        }
      } else {
        setSelectedSubmission(null);
        setSelectedQuestion(null);
        setEditData({});
      }
    } else if (submissionsArray.length > 0 && !currentSelectedSubmission) {
      setSelectedSubmission(submissionsArray[0]);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      // Fetch reference data
      try {
        const [topicsRes, functionsRes, surveyorsRes] = await Promise.all([
          axios.get(`${API}/topics`),
          axios.get(`${API}/functions`),
          axios.get(`${API}/surveyors`)
        ]);
        setTopics(topicsRes.data);
        setFunctions(functionsRes.data);
        setSurveyors(surveyorsRes.data);
      } catch (error) {
        console.error('Failed to fetch reference data:', error);
      }

      // Fetch pending questions
      try {
        setLoading(true);
        const response = await axios.get(`${API}/admin/questions/pending`);
        const questions = response.data;
        setPendingQuestions(questions);
        groupQuestionsIntoSubmissions(questions);
      } catch (error) {
        console.error('Failed to fetch pending questions:', error);
        toast.error('Failed to load pending questions');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);
  
  // Helper function to refetch and regroup submissions while preserving selection
  const refetchAndRegroup = async (preserveBatchId = null) => {
    try {
      const response = await axios.get(`${API}/admin/questions/pending`);
      const freshQuestions = response.data;
      setPendingQuestions(freshQuestions);
      
      // Group and fetch user details, passing current state for preservation logic
      await groupQuestionsIntoSubmissions(freshQuestions, preserveBatchId, selectedQuestion, selectedSubmission);
      
      return freshQuestions;
    } catch (error) {
      console.error('Failed to refetch questions:', error);
      return null;
    }
  };

  // Find the first non-empty surveyor for the given side across the batch's
  // sibling questions. Used to pre-fill the opposite side when an admin flips
  // `asked_by_internal` in Review, so Q1 flipping to external inherits Q2's
  // external surveyor (they came from the same oral session). Falls back to
  // `null` if no sibling in the batch has that side set.
  const getBatchSiblingSurveyor = (side) => {
    if (!selectedSubmission || !selectedSubmission.questions) return null;
    const field = side === 'internal' ? 'surveyor_internal_id' : 'surveyor_external_id';
    for (const q of selectedSubmission.questions) {
      if (q[field]) return q[field];
    }
    return null;
  };

  const handleToggleAskedByInternal = (checked) => {
    const next = { ...editData, asked_by_internal: checked };
    if (checked) {
      // Flipping to internal — if no internal surveyor is set on this row,
      // inherit from a sibling in the same batch.
      if (!next.surveyor_internal_id) {
        const inherited = getBatchSiblingSurveyor('internal');
        if (inherited) next.surveyor_internal_id = inherited;
      }
    } else {
      // Flipping to external — same idea, but for the external side.
      if (!next.surveyor_external_id) {
        const inherited = getBatchSiblingSurveyor('external');
        if (inherited) next.surveyor_external_id = inherited;
      }
    }
    setEditData(next);
  };

  const handleSelectQuestion = (question) => {
    setSelectedQuestion(question);
    setEditData({
      question_text: question.question_text,
      answer_text: question.answer_text || '',
      topic_id: question.topic_id || '',
      asked_by_internal: question.asked_by_internal || false,
      surveyor_internal_id: question.surveyor_internal_id || null,
      surveyor_external_id: question.surveyor_external_id || null
    });
    // Hydrate multi-select topic state to mirror what's on the question.
    const ids = question.topic_ids?.length > 0
      ? question.topic_ids
      : (question.topic_id ? [question.topic_id] : []);
    setSelectedTopicIds(ids);
    setTopicSearchQuery('');
    // Similar questions are fetched automatically by useEffect watching selectedQuestion
  };

  const fetchSimilarQuestions = async (questionId) => {
    if (!questionId) {
      setSimilarQuestions([]);
      return;
    }
    try {
      setLoadingSimilar(true);
      const response = await axios.get(`${API}/admin/questions/${questionId}/find-similar`);
      setSimilarQuestions(response.data);
    } catch (error) {
      console.error('Failed to fetch similar questions:', error);
      setSimilarQuestions([]);
    } finally {
      setLoadingSimilar(false);
    }
  };

  // Fetch similar questions whenever selectedQuestion changes (manual or auto-selected)
  useEffect(() => {
    if (selectedQuestion?.id) {
      fetchSimilarQuestions(selectedQuestion.id);
    } else {
      setSimilarQuestions([]);
    }
  }, [selectedQuestion?.id]);

  // Open merge dialog with a target question
  const openMergeDialog = (targetQuestion) => {
    setMergeTargetQuestion(targetQuestion);
    setMergeOptions({
      updateAnswer: false
    });
    setMergeDialogOpen(true);
  };

  // Handle merge with existing question
  const handleMergeWithExisting = async () => {
    if (!selectedQuestion || !mergeTargetQuestion) return;
    
    setIsMerging(true);
    try {
      const currentBatchId = selectedSubmission?.id;
      
      await axios.post(`${API}/admin/questions/merge`, {
        source_question_id: selectedQuestion.id,
        target_question_id: mergeTargetQuestion.id,
        update_answer: mergeOptions.updateAnswer,
        merge_pictures: false,  // Users can't submit pictures, so always false
        source_type: 'pending'
      });
      
      toast.success('Question merged successfully! Surveyor added to question history.');
      
      setMergeDialogOpen(false);
      setMergeTargetQuestion(null);
      setSimilarQuestions([]);
      
      // Refetch and regroup
      await refetchAndRegroup(currentBatchId);
      
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to merge questions:', error);
      toast.error(error.response?.data?.detail || 'Failed to merge questions');
    } finally {
      setIsMerging(false);
    }
  };

  // Filter topics based on the question's grade and function
  const getFilteredTopics = (question) => {
    if (!question) return [];
    return topics.filter(
      t => t.grade === question.grade && t.function_id === question.function_id
    );
  };

  // Hierarchical version: parents first, each followed by its children.
  // Matches the shape rendered by AdminQuestionsPage so admins see a
  // consistent topic picker whether they're editing a live question or
  // reviewing a user-submitted pending one.
  const getFilteredTopicsHierarchical = (question) => {
    const available = getFilteredTopics(question);
    if (available.length === 0) return [];
    const byId = new Map(available.map(t => [t.id, t]));
    const parents = available.filter(t => !t.parent_topic_id);
    const orphans = available.filter(t => t.parent_topic_id && !byId.has(t.parent_topic_id));
    const ordered = [];
    parents.forEach(p => {
      ordered.push({ ...p, depth: 0 });
      available
        .filter(c => c.parent_topic_id === p.id)
        .forEach(c => ordered.push({ ...c, depth: 1, parent_name: p.name }));
    });
    orphans.forEach(o => ordered.push({ ...o, depth: 0 }));
    return ordered;
  };

  const handleAddTopic = (topicId) => {
    if (!selectedTopicIds.includes(topicId)) {
      setSelectedTopicIds([...selectedTopicIds, topicId]);
    }
    setTopicSearchQuery('');
  };

  const handleRemoveTopic = (topicId) => {
    setSelectedTopicIds(selectedTopicIds.filter(id => id !== topicId));
  };

  // Get topic name by ID
  const getTopicName = (topicId) => {
    if (!topicId) return '';
    const topic = topics.find(t => t.id === topicId);
    return topic ? topic.name : '';
  };

  // Get function name by ID
  const getFunctionName = (functionId) => {
    if (!functionId) return 'Unknown';
    const func = functions.find(f => f.id === functionId);
    return func ? func.name : 'Unknown';
  };

  // Get surveyor name by ID
  const getSurveyorName = (surveyorId) => {
    if (!surveyorId) return null;
    const surveyor = surveyors.find(s => s.id === surveyorId);
    if (!surveyor) return 'Unknown';
    return surveyor.is_active === false ? `${surveyor.name} (Inactive)` : surveyor.name;
  };

  // Get unique functions in a submission
  const getUniqueFunctions = (submission) => {
    if (!submission || !submission.questions) return [];
    const functionIds = [...new Set(submission.questions.map(q => q.function_id))];
    return functionIds.map(fid => ({
      id: fid,
      name: getFunctionName(fid),
      questions: submission.questions.filter(q => q.function_id === fid)
    }));
  };

  // Get question count by status in submission
  const getSubmissionStats = (submission) => {
    if (!submission || !submission.questions) return { total: 0, approved: 0, rejected: 0 };
    return {
      total: submission.questions.length,
      approved: submission.questions.filter(q => q.status === 'approved').length,
      rejected: submission.questions.filter(q => q.status === 'rejected').length,
      functions: new Set(submission.questions.map(q => q.function_id)).size
    };
  };

  const handleUpdateQuestion = async () => {
    try {
      // Enforce single-surveyor policy based on asked_by_internal
      let updatePayload = { ...editData };

      // Multi-topic: send the full array. Keep the legacy `topic_id`
      // field in sync (first selected topic) so any older read-path
      // that still reads `topic_id` continues to see something sensible.
      updatePayload.topic_ids = selectedTopicIds;
      updatePayload.topic_id = selectedTopicIds.length > 0 ? selectedTopicIds[0] : null;

      if (editData.asked_by_internal) {
        // If asked by internal, clear external surveyor
        updatePayload.surveyor_external_id = null;
        // If no internal surveyor selected, assign to Unknown Internal
        if (!updatePayload.surveyor_internal_id) {
          updatePayload.surveyor_internal_id = 'unknown-internal-active';
        }
      } else {
        // If asked by external, clear internal surveyor
        updatePayload.surveyor_internal_id = null;
        // If no external surveyor selected, assign to Unknown External
        if (!updatePayload.surveyor_external_id) {
          updatePayload.surveyor_external_id = 'unknown-external-active';
        }
      }

      await axios.put(`${API}/questions/${selectedQuestion.id}`, updatePayload);
      toast.success('Question updated successfully');
      setSelectedQuestion({ ...selectedQuestion, ...updatePayload });
      
      // Update editData to reflect changes
      setEditData(updatePayload);
    } catch (error) {
      console.error('Failed to update question:', error);
      toast.error(error.response?.data?.detail || 'Failed to update question');
    }
  };

  const handleApprove = async (questionId) => {
    try {
      // Save current submission batch ID to restore selection after refetch
      const currentBatchId = selectedSubmission?.id;
      
      await axios.put(`${API}/admin/questions/${questionId}/approve`);
      toast.success('Question approved successfully');
      
      // Refetch and regroup with selection preservation
      await refetchAndRegroup(currentBatchId);
      
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to approve question:', error);
      toast.error('Failed to approve question');
    }
  };

  const handleReject = async (questionId) => {
    // Show confirmation dialog
    if (!window.confirm('⚠️ Rejecting this question will DELETE it permanently from the database.\n\nThis action cannot be undone. Are you sure you want to proceed?')) {
      return;
    }

    try {
      // Save current submission batch ID to restore selection after refetch
      const currentBatchId = selectedSubmission?.id;
      
      // Delete the question instead of just rejecting it
      await axios.delete(`${API}/questions/${questionId}`);
      toast.success('Question rejected and deleted successfully');
      
      // Refetch and regroup with selection preservation
      await refetchAndRegroup(currentBatchId);
      
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to reject question:', error);
      toast.error('Failed to reject question');
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Column 1: Submissions List (30%) */}
      <Card className="col-span-12 md:col-span-3">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Submissions ({submissions.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner"></div>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No pending submissions</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[700px] overflow-y-auto">
              {submissions.map((submission, index) => {
                const stats = getSubmissionStats(submission);
                const date = submission.created_at ? new Date(submission.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'Unknown';
                
                return (
                  <Card
                    key={submission.id}
                    onClick={() => {
                      setSelectedSubmission(submission);
                      setSelectedQuestion(null); // Clear selected question when changing submission
                    }}
                    className={`cursor-pointer transition-all ${
                      selectedSubmission?.id === submission.id
                        ? 'border-2 border-[#0A7EA4] bg-[#0A7EA4]/5'
                        : 'border hover:border-[#0A7EA4]/50'
                    }`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-xs font-semibold text-gray-700">Submission #{getSubmissionNumber(submission.id)}</p>
                          <p className="text-xs text-gray-500">{date}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setBulkEditSubmission(submission);
                            }}
                            className="p-1 hover:bg-amber-100 rounded transition-colors"
                            title="Bulk edit surveyors"
                          >
                            <Settings className="w-4 h-4 text-amber-600" />
                          </button>
                          <Badge className="bg-indigo-600 text-xs">{stats.total} Qs</Badge>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-xs">
                          <Badge variant="outline" className="text-xs">{submission.grade}</Badge>
                          <Badge variant="outline" className="text-xs">{submission.mmd_location}</Badge>
                        </div>
                        <p className="text-xs text-gray-600">
                          {stats.functions} {stats.functions === 1 ? 'Function' : 'Functions'}
                        </p>
                        <p className="text-xs text-gray-500">
                          By: {submission.user_indos || 'Loading...'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Column 2: Questions List by Function (30%) */}
      <Card className="col-span-12 md:col-span-3">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            Questions
            {selectedSubmission && ` (${selectedSubmission.questions.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          {!selectedSubmission ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm">Select a submission to view questions</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[700px] overflow-y-auto">
              {getUniqueFunctions(selectedSubmission).map((func) => (
                <div key={func.id}>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 sticky top-0 bg-white pb-1">
                    {func.name} ({func.questions.length})
                  </h3>
                  <div className="space-y-2">
                    {func.questions.map((question, qIndex) => (
                      <Card
                        key={question.id}
                        onClick={() => handleSelectQuestion(question)}
                        className={`cursor-pointer transition-all ${
                          selectedQuestion?.id === question.id
                            ? 'border-2 border-[#8B5CF6] bg-[#8B5CF6]/5'
                            : 'border hover:border-[#8B5CF6]/50'
                        }`}
                      >
                        <CardContent className="p-2">
                          <div className="flex items-start justify-between mb-1">
                            <span className="text-xs font-medium text-gray-600">Q{qIndex + 1}</span>
                            {question.status === 'approved' && (
                              <CheckCircle className="w-3 h-3 text-green-600" />
                            )}
                          </div>
                          <p className="text-xs text-gray-800 line-clamp-2">
                            {question.question_text}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Column 3: Question Detail & Edit (40%) */}
      <Card className="col-span-12 md:col-span-6">
        <CardHeader>
          <CardTitle className="text-lg">Question Details</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {!selectedQuestion ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Select a question to review</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Metadata */}
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <span className="text-sm text-gray-600">Grade:</span>
                  <Badge className="ml-2 bg-[#0A7EA4]">{selectedQuestion.grade}</Badge>
                </div>
                <div>
                  <span className="text-sm text-gray-600">MMD:</span>
                  <Badge className="ml-2 bg-[#14B8A6]">{selectedQuestion.mmd_location}</Badge>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Function:</span>
                  <span className="ml-2 text-sm font-medium">
                    {getFunctionName(selectedQuestion.function_id) || 'Loading...'}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Topic{(selectedQuestion.topic_ids?.length > 1) ? 's' : ''}:</span>
                  {(selectedQuestion.topic_ids?.length > 0 || selectedQuestion.topic_id) ? (
                    <span className="ml-2 inline-flex flex-wrap gap-1">
                      {(selectedQuestion.topic_ids?.length > 0 ? selectedQuestion.topic_ids : [selectedQuestion.topic_id])
                        .map(tid => getTopicName(tid))
                        .filter(Boolean)
                        .map((topicName, idx) => (
                          <Badge key={idx} className="bg-purple-600 flex items-center gap-1 w-fit">
                            <Tag className="w-3 h-3" />
                            {topicName}
                          </Badge>
                        ))
                      }
                    </span>
                  ) : (
                    <Badge className="ml-2" variant="outline">Not Assigned</Badge>
                  )}
                </div>
                <div>
                  <span className="text-sm text-gray-600">Exam Month:</span>
                  <span className="ml-2 text-sm font-medium">{selectedQuestion.exam_month}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Decider:</span>
                  <Badge className="ml-2" variant={selectedQuestion.is_decider ? 'default' : 'outline'}>
                    {selectedQuestion.is_decider ? 'Yes' : 'No'}
                  </Badge>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Asked By:</span>
                  <Badge className="ml-2 bg-indigo-600">
                    {selectedQuestion.asked_by_internal ? 'Internal' : 'External'}
                  </Badge>
                </div>
                <div className="md:col-span-2">
                  <span className="text-sm text-gray-600">Surveyor:</span>
                  <span className="ml-2 text-sm font-medium">
                    {selectedQuestion.surveyor_internal_id ? (
                      <>Internal: {getSurveyorName(selectedQuestion.surveyor_internal_id)}</>
                    ) : selectedQuestion.surveyor_external_id ? (
                      <>External: {getSurveyorName(selectedQuestion.surveyor_external_id)}</>
                    ) : (
                      'Not assigned'
                    )}
                  </span>
                </div>
              </div>

              {/* User-entered surveyor names (if any) */}
              {(selectedQuestion.surveyor_internal_other || selectedQuestion.surveyor_external_other) && (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
                  <p className="text-sm font-semibold text-amber-800 mb-2">⚠️ User entered surveyor name(s):</p>
                  {selectedQuestion.surveyor_internal_other && (
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Internal:</span> {selectedQuestion.surveyor_internal_other}
                    </p>
                  )}
                  {selectedQuestion.surveyor_external_other && (
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">External:</span> {selectedQuestion.surveyor_external_other}
                    </p>
                  )}
                  <p className="text-xs text-amber-700 mt-2">
                    Please find and assign the correct surveyor below before approving
                  </p>
                </div>
              )}

              <Tabs defaultValue="edit" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="edit">Edit Question</TabsTrigger>
                  <TabsTrigger value="original">View Original</TabsTrigger>
                </TabsList>

                <TabsContent value="edit" className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="edit-question">Question</Label>
                    <Textarea
                      id="edit-question"
                      data-testid="admin-edit-question"
                      className="mt-1"
                      rows={4}
                      value={editData.question_text}
                      onChange={(e) => setEditData({ ...editData, question_text: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit-answer">Answer</Label>
                    <Textarea
                      id="edit-answer"
                      data-testid="admin-edit-answer"
                      className="mt-1"
                      rows={8}
                      value={editData.answer_text}
                      onChange={(e) => setEditData({ ...editData, answer_text: e.target.value })}
                      placeholder="Add or improve the answer (include explanations, details, references here)..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Write the complete answer with explanations and details in one place
                    </p>
                  </div>

                  {/* Topic Assignment — iter 105.4: swapped custom
                      2-level tag picker for the shared
                      <TopicPickerMulti> (checkbox tree + searchable
                      breadcrumb list + redundancy hint). Was hard-coded
                      to `topic.depth === 1` styling so grandchild+
                      topics rendered at the wrong indent. */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <Label className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-purple-600" />
                      <span className="text-purple-900">Assign Topics</span>
                    </Label>
                    <TopicPickerMulti
                      topics={getFilteredTopics(selectedQuestion)}
                      value={selectedTopicIds}
                      onChange={setSelectedTopicIds}
                      placeholder="Tag topics for this question…"
                      testIdPrefix="admin-review-topic-tags"
                    />
                    {getFilteredTopics(selectedQuestion).length === 0 && (
                      <p className="text-xs text-gray-500 mt-2">
                        No topics available for {selectedQuestion.grade} — {getFunctionName(selectedQuestion.function_id)}
                      </p>
                    )}
                    <p className="text-xs text-purple-600 mt-2">
                      💡 You can assign multiple topics
                    </p>
                  </div>
                  {/* Surveyor Assignment Section */}
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-300 space-y-3">
                    <h4 className="font-semibold text-amber-900">Surveyor Assignment</h4>
                    
                    {/* Asked by Internal Checkbox */}
                    <div className="flex items-start space-x-3 p-3 bg-white border border-amber-400 rounded">
                      <input
                        type="checkbox"
                        id="edit-asked-by-internal"
                        data-testid="edit-asked-by-internal-checkbox"
                        checked={editData.asked_by_internal}
                        onChange={(e) => handleToggleAskedByInternal(e.target.checked)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <Label htmlFor="edit-asked-by-internal" className="cursor-pointer font-medium text-sm">
                          Asked by Internal Surveyor
                        </Label>
                        <p className="text-xs text-gray-600 mt-1">
                          ✓ Checked = Internal only | ✗ Unchecked = External only
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          Admin override: Change if user made a mistake
                        </p>
                      </div>
                    </div>

                    {/* Internal Surveyor - Only show if asked_by_internal is checked - Searchable */}
                    {editData.asked_by_internal && (
                      <div>
                        <Label htmlFor="edit-internal-surveyor">Internal Surveyor *</Label>
                        <Popover open={internalSurveyorComboOpen} onOpenChange={setInternalSurveyorComboOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={internalSurveyorComboOpen}
                              className="w-full justify-between mt-1"
                            >
                              {editData.surveyor_internal_id 
                                ? surveyors.find(s => s.id === editData.surveyor_internal_id)?.name || 'Select surveyor...'
                                : 'Select internal surveyor...'}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search internal surveyors..." />
                              <CommandList>
                                <CommandEmpty>No surveyors found.</CommandEmpty>
                                {(() => {
                                  const questionIsNautical = isNauticalGrade(selectedQuestion.grade);
                                  const mmd = selectedQuestion.mmd_location;
                                  const mmdNormalized = mmd?.endsWith(' MMD') ? mmd : `${mmd} MMD`;
                                  const filteredInternal = surveyors.filter(s => 
                                    s.type === 'internal' && 
                                    s.grades?.includes(selectedQuestion.grade) &&
                                    (questionIsNautical || s.mmd_locations?.includes(mmd) || s.mmd_locations?.includes(mmdNormalized))
                                  );
                                  
                                  if (filteredInternal.length === 0) {
                                    return (
                                      <div className="p-2 text-xs text-gray-500 text-center">
                                        No internal surveyors found
                                      </div>
                                    );
                                  }
                                  
                                  if (questionIsNautical) {
                                    // Group by MMD for Nautical
                                    const grouped = {};
                                    filteredInternal.forEach(s => {
                                      const mmd = s.mmd_locations?.[0] || 'Unknown';
                                      if (!grouped[mmd]) grouped[mmd] = [];
                                      grouped[mmd].push(s);
                                    });
                                    return Object.entries(grouped).map(([mmd, mmdSurveyors]) => (
                                      <CommandGroup key={mmd} heading={mmd}>
                                        {mmdSurveyors.map(s => (
                                          <CommandItem
                                            key={s.id}
                                            value={`${s.name} ${mmd}`}
                                            onSelect={() => {
                                              setEditData({ ...editData, surveyor_internal_id: s.id });
                                              setInternalSurveyorComboOpen(false);
                                            }}
                                          >
                                            <Check className={cn("mr-2 h-4 w-4", editData.surveyor_internal_id === s.id ? "opacity-100" : "opacity-0")} />
                                            {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    ));
                                  } else {
                                    // Flat list for Engineering
                                    return (
                                      <CommandGroup>
                                        {filteredInternal.map(s => (
                                          <CommandItem
                                            key={s.id}
                                            value={s.name}
                                            onSelect={() => {
                                              setEditData({ ...editData, surveyor_internal_id: s.id });
                                              setInternalSurveyorComboOpen(false);
                                            }}
                                          >
                                            <Check className={cn("mr-2 h-4 w-4", editData.surveyor_internal_id === s.id ? "opacity-100" : "opacity-0")} />
                                            {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    );
                                  }
                                })()}
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <p className="text-xs text-gray-500 mt-1">
                          {isNauticalGrade(selectedQuestion.grade)
                            ? `Showing all ${selectedQuestion.grade} surveyors (any MMD)`
                            : `Filtered by ${selectedQuestion.mmd_location} (includes active & inactive)`}
                        </p>
                      </div>
                    )}

                    {/* External Surveyor - Only show if asked_by_internal is NOT checked - Searchable */}
                    {!editData.asked_by_internal && (
                      <div>
                        <Label htmlFor="edit-external-surveyor">External Surveyor *</Label>
                        <Popover open={externalSurveyorComboOpen} onOpenChange={setExternalSurveyorComboOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={externalSurveyorComboOpen}
                              className="w-full justify-between mt-1"
                            >
                              {editData.surveyor_external_id 
                                ? surveyors.find(s => s.id === editData.surveyor_external_id)?.name || 'Select surveyor...'
                                : 'Select external surveyor...'}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search external surveyors..." />
                              <CommandList>
                                <CommandEmpty>No surveyors found.</CommandEmpty>
                                {(() => {
                                  const questionIsNautical = isNauticalGrade(selectedQuestion.grade);
                                  const mmd = selectedQuestion.mmd_location;
                                  const mmdNormalized = mmd?.endsWith(' MMD') ? mmd : `${mmd} MMD`;
                                  const filteredExternal = surveyors.filter(s => 
                                    s.type === 'external' && 
                                    s.grades?.includes(selectedQuestion.grade) &&
                                    (questionIsNautical || s.mmd_locations?.includes(mmd) || s.mmd_locations?.includes(mmdNormalized))
                                  );
                                  
                                  if (filteredExternal.length === 0) {
                                    return (
                                      <div className="p-2 text-xs text-gray-500 text-center">
                                        No external surveyors found
                                      </div>
                                    );
                                  }
                                  
                                  if (questionIsNautical) {
                                    // Group by MMD for Nautical
                                    const grouped = {};
                                    filteredExternal.forEach(s => {
                                      const mmd = s.mmd_locations?.[0] || 'Unknown';
                                      if (!grouped[mmd]) grouped[mmd] = [];
                                      grouped[mmd].push(s);
                                    });
                                    return Object.entries(grouped).map(([mmd, mmdSurveyors]) => (
                                      <CommandGroup key={mmd} heading={mmd}>
                                        {mmdSurveyors.map(s => (
                                          <CommandItem
                                            key={s.id}
                                            value={`${s.name} ${mmd}`}
                                            onSelect={() => {
                                              setEditData({ ...editData, surveyor_external_id: s.id });
                                              setExternalSurveyorComboOpen(false);
                                            }}
                                          >
                                            <Check className={cn("mr-2 h-4 w-4", editData.surveyor_external_id === s.id ? "opacity-100" : "opacity-0")} />
                                            {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    ));
                                  } else {
                                    // Flat list for Engineering
                                    return (
                                      <CommandGroup>
                                        {filteredExternal.map(s => (
                                          <CommandItem
                                            key={s.id}
                                            value={s.name}
                                            onSelect={() => {
                                              setEditData({ ...editData, surveyor_external_id: s.id });
                                              setExternalSurveyorComboOpen(false);
                                            }}
                                          >
                                            <Check className={cn("mr-2 h-4 w-4", editData.surveyor_external_id === s.id ? "opacity-100" : "opacity-0")} />
                                            {s.name}{s.is_active === false ? ' (Inactive)' : ''}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    );
                                  }
                                })()}
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <p className="text-xs text-gray-500 mt-1">
                          {isNauticalGrade(selectedQuestion.grade)
                            ? `Showing all ${selectedQuestion.grade} surveyors (any MMD)`
                            : `Filtered by ${selectedQuestion.mmd_location} (includes active & inactive)`}
                        </p>
                      </div>
                    )}
                  </div>

                  <Button
                    data-testid="admin-update-btn"
                    onClick={handleUpdateQuestion}
                    className="w-full bg-[#14B8A6] hover:bg-[#14B8A6]/90"
                  >
                    Update Question
                  </Button>
                </TabsContent>

                <TabsContent value="original" className="space-y-4 mt-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Original Question:</h4>
                    <p className="text-gray-800">{selectedQuestion.question_text}</p>
                  </div>
                  {selectedQuestion.answer_text && (
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-semibold mb-2">Original Answer:</h4>
                      <p className="text-gray-800">{selectedQuestion.answer_text}</p>
                    </div>
                  )}
                  {selectedQuestion.explanation && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold mb-2">Original Explanation:</h4>
                      <p className="text-gray-800">{selectedQuestion.explanation}</p>
                    </div>
                  )}
                  {selectedQuestion.references && (
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h4 className="font-semibold mb-2">Original References:</h4>
                      <p className="text-gray-800">{selectedQuestion.references}</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Similar Questions Section */}
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-amber-900">
                    🔍 Similar Questions Found
                  </h3>
                  {loadingSimilar && (
                    <span className="text-sm text-amber-600">Searching...</span>
                  )}
                </div>
                
                {loadingSimilar ? (
                  <p className="text-sm text-amber-700">Analyzing question for duplicates...</p>
                ) : similarQuestions.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-sm text-amber-700 mb-3">
                      Found {similarQuestions.length} potentially similar question(s). Review below options.
                    </p>
                    {similarQuestions.map((similar) => (
                      <div key={similar.id} className="p-3 bg-white border border-amber-300 rounded-lg">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge className={
                                similar.similarity_score >= 80 ? "bg-red-600" :
                                similar.similarity_score >= 65 ? "bg-amber-600" :
                                "bg-blue-600"
                              }>
                                {similar.similarity_score.toFixed(1)}% Semantic
                              </Badge>
                              <Badge className="bg-[#0A7EA4]">{similar.grade}</Badge>
                              <Badge className="bg-[#14B8A6]">{similar.mmd_location}</Badge>
                              <Badge className="bg-orange-600">
                                F{similar.function_number}{similar.function_suffix && similar.function_suffix !== 'None' && similar.function_suffix !== 'null' ? similar.function_suffix : ''} - {similar.function_name}
                              </Badge>
                              {similar.asked > 1 && (
                                <Badge className="bg-purple-600">
                                  Asked {similar.asked}x
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              {similar.question_text}
                            </p>
                            {similar.answer_text && (
                              <p className="text-xs text-gray-600 mt-1">
                                <strong>Answer:</strong> {similar.answer_text.substring(0, 150)}
                                {similar.answer_text.length > 150 ? '...' : ''}
                              </p>
                            )}
                            {similar.exam_month && (
                              <p className="text-xs text-gray-500 mt-1">
                                Exam: {similar.exam_month}
                              </p>
                            )}
                            {(similar.surveyor_internal_name || similar.surveyor_external_name) && (
                              <p className="text-xs text-gray-500 mt-1">
                                <strong>Surveyor:</strong>{' '}
                                {similar.surveyor_internal_name && (
                                  <span className="text-blue-600">{similar.surveyor_internal_name} (Internal)</span>
                                )}
                                {similar.surveyor_internal_name && similar.surveyor_external_name && ' / '}
                                {similar.surveyor_external_name && (
                                  <span className="text-green-600">{similar.surveyor_external_name} (External)</span>
                                )}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              onClick={() => openMergeDialog(similar)}
                              className="bg-[#0A7EA4] hover:bg-[#086b8a] text-white"
                            >
                              Merge with Existing
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-green-700">
                    ✓ No similar questions found. This appears to be a unique question.
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  data-testid="admin-approve-btn"
                  onClick={() => handleApprove(selectedQuestion.id)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  data-testid="admin-reject-btn"
                  onClick={() => handleReject(selectedQuestion.id)}
                  variant="outline"
                  className="flex-1 text-red-600 hover:bg-red-50 border-red-600"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Edit Dialog */}
      <Dialog open={!!bulkEditSubmission} onOpenChange={(open) => !open && setBulkEditSubmission(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Edit Surveyors</DialogTitle>
          </DialogHeader>
          {bulkEditSubmission && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-sm font-medium">Updating {bulkEditSubmission.questions.length} questions</p>
                <p className="text-xs text-gray-600">{bulkEditSubmission.grade} • {bulkEditSubmission.mmd_location}</p>
              </div>
              <BulkSurveyorEdit 
                submission={bulkEditSubmission}
                surveyors={surveyors}
                onUpdate={async (updatedQuestions) => {
                  // Save current submission batch ID to restore selection after refetch
                  const currentBatchId = selectedSubmission?.id;
                  
                  // Refetch and regroup with selection preservation
                  await refetchAndRegroup(currentBatchId);
                  
                  // Update editData if selected question was updated
                  if (selectedQuestion) {
                    const updatedQuestion = updatedQuestions.find(q => q.id === selectedQuestion.id);
                    if (updatedQuestion) {
                      setEditData({
                        question_text: updatedQuestion.question_text,
                        answer_text: updatedQuestion.answer_text || '',
                        topic_id: updatedQuestion.topic_id || '',
                        asked_by_internal: updatedQuestion.asked_by_internal || false,
                        surveyor_internal_id: updatedQuestion.surveyor_internal_id || null,
                        surveyor_external_id: updatedQuestion.surveyor_external_id || null
                      });
                    }
                  }
                  
                  setBulkEditSubmission(null);
                  toast.success(`Updated ${updatedQuestions.length} questions`);
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Merge Question Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setMergeDialogOpen(false);
          setMergeTargetQuestion(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Merge with Existing Question</DialogTitle>
            <DialogDescription>
              Add the new surveyor to the existing question's history. The pending question will be removed.
            </DialogDescription>
          </DialogHeader>
          
          {selectedQuestion && mergeTargetQuestion && (
            <div className="space-y-4">
              {/* Comparison View */}
              <div className="grid grid-cols-2 gap-4">
                {/* New Submission */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">New Submission</h4>
                  <p className="text-xs text-gray-600 mb-1">
                    <strong>Surveyor:</strong> {getSurveyorName(selectedQuestion.asked_by_internal ? selectedQuestion.surveyor_internal_id : selectedQuestion.surveyor_external_id) || 'Not assigned'}
                    {selectedQuestion.asked_by_internal ? ' (Internal)' : ' (External)'}
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    <strong>Exam:</strong> {selectedQuestion.exam_month || 'Not specified'}
                  </p>
                  <p className="text-sm text-gray-700">{selectedQuestion.question_text}</p>
                  {selectedQuestion.answer_text && (
                    <div className="mt-2 p-2 bg-white rounded text-xs">
                      <strong>Answer:</strong> {selectedQuestion.answer_text.substring(0, 200)}
                      {selectedQuestion.answer_text.length > 200 ? '...' : ''}
                    </div>
                  )}
                </div>

                {/* Existing Question */}
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-green-800 mb-2">Existing Question</h4>
                  <p className="text-xs text-gray-600 mb-1">
                    <strong>Surveyor:</strong> {mergeTargetQuestion.surveyor_internal_name || mergeTargetQuestion.surveyor_external_name || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-600 mb-1">
                    <strong>Asked:</strong> {mergeTargetQuestion.asked || 1}x
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    <strong>Exam:</strong> {mergeTargetQuestion.exam_month || 'Not specified'}
                  </p>
                  <p className="text-sm text-gray-700">{mergeTargetQuestion.question_text}</p>
                  {mergeTargetQuestion.answer_text && (
                    <div className="mt-2 p-2 bg-white rounded text-xs">
                      <strong>Answer:</strong> {mergeTargetQuestion.answer_text.substring(0, 200)}
                      {mergeTargetQuestion.answer_text.length > 200 ? '...' : ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Merge Options */}
              <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                <h4 className="text-sm font-semibold text-gray-700">Merge Options</h4>
                
                {/* Only show update answer option if the source submission has an answer */}
                {selectedQuestion?.answer_text && selectedQuestion.answer_text.trim() !== '' && selectedQuestion.answer_text !== 'Coming soon' ? (
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="updateAnswer"
                      checked={mergeOptions.updateAnswer}
                      onCheckedChange={(checked) => setMergeOptions(prev => ({ ...prev, updateAnswer: checked }))}
                    />
                    <div>
                      <label htmlFor="updateAnswer" className="text-sm font-medium cursor-pointer">
                        Update answer with new submission
                      </label>
                      <p className="text-xs text-gray-500">Replace the existing answer with the new one</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 italic">No additional merge options available (submission has no answer)</p>
                )}
              </div>

              {/* What will happen */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-amber-800">
                    <p className="font-medium mb-1">After merge:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>New surveyor will be added to question history</li>
                      <li>Total "asked" count will be updated</li>
                      <li>The pending question will be removed</li>
                      <li>Question will appear in filter results for both surveyors</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setMergeDialogOpen(false);
                setMergeTargetQuestion(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMergeWithExisting}
              disabled={isMerging}
              className="bg-[#0A7EA4] hover:bg-[#086b8a]"
            >
              {isMerging ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                'Confirm Merge'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionReview;